import { Component, DestroyRef, inject, output } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter, firstValueFrom, map, startWith, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthService } from 'core/services/auth.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LangTranslateBarComponent } from 'shared/components/lang-translate-bar/lang-translate-bar.component';
import { CommonModule } from '@angular/common';
import { NzMenuModule } from 'ng-zorro-antd/menu';
import { GoBackComponent } from 'shared/components/go-back/go-back.component';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { SearchComponent } from './search/search.component';
import {
  Brand,
  Faq,
  Ganacuate,
  Ganamigo,
  Ganarbol,
  Product,
  SearchResult,
  SearchResultTypes,
  Seed
} from 'shared/types/search-result.type';
import { SearchService } from 'core/services/search.service';
import { UsersService } from 'core/services/users.service';
import { RefreshOnLanguageChangeDirective } from 'shared/directives/refresh-on-language-change.directive';

@Component({
  selector: 'workspace-header',
  standalone: true,
  imports: [
    LangTranslateBarComponent,
    CommonModule,
    NzIconModule,
    NzButtonModule,
    NzMenuModule,
    TranslateModule,
    GoBackComponent,
    SearchComponent
  ],
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  hostDirectives: [RefreshOnLanguageChangeDirective]
})
export class HeaderComponent {
  readonly #destroyRef = inject(DestroyRef);
  readonly #translateService = inject(TranslateService);
  readonly #router = inject(Router);
  readonly #route = inject(ActivatedRoute);
  readonly #searchService = inject(SearchService);
  readonly #usersService = inject(UsersService);

  public readonly leftSideBarUncollapseClick = output<void>();
  public readonly searchFn = this.#searchService.globalSearch.bind(this.#searchService);

  public isMenuOpen = false;
  public settingsPopoverVisible = false;
  public notificationsPopoverVisible = false;
  public currentSectionLocalizableName = '';

  public get selectedLanguage(): string {
    return this.#translateService.currentLang;
  }

  public readonly authService = inject(AuthService);

  private readonly _navigationMap = new Map<SearchResultTypes, (result: SearchResult) => Promise<boolean>>([
    [SearchResultTypes.SEED, this._navigateToSeed.bind(this)],
    [SearchResultTypes.BRAND, this._navigateToBrand.bind(this)],
    [SearchResultTypes.PRODUCT, this._navigateToProduct.bind(this)],
    [SearchResultTypes.GANARBOL, this._navigateToGanarbol.bind(this)],
    [SearchResultTypes.GANACUATE, this._navigateToGanacuate.bind(this)],
    [SearchResultTypes.GANAMIGO, this._navigateToGanamigo.bind(this)],
    [SearchResultTypes.FAQ, this._navigateToFaq.bind(this)]
  ]);

  public ngOnInit(): void {
    this.#router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        startWith(null),
        map(() => this._getCurrentRouteData()),
        tap(data => {
          this.currentSectionLocalizableName = this._getSectionName(data);
        }),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe();
  }

  public goTo(url: string): void {
    this.#router.navigate([url]);
  }

  public useLanguage(language: string): void {
    this.#translateService.use(language);
    this.isMenuOpen = false;
  }

  public onSearchResultClick(result: SearchResult): void {
    const navigate = this._navigationMap.get(result?.type);
    if (!navigate) {
      return;
    }

    void navigate(result);
  }

  public onUncollapseClick(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.leftSideBarUncollapseClick.emit();
  }

  private _getCurrentRouteData(): Record<string, unknown> | undefined {
    return this.#route.firstChild?.firstChild?.snapshot.data;
  }

  private _getSectionName(data?: Record<string, unknown>): string {
    const title = data?.['title'];
    const breadcrumb = data?.['breadcrumb'];

    if (typeof title === 'string' && title) {
      return title;
    }

    if (typeof breadcrumb === 'string' && breadcrumb) {
      return breadcrumb;
    }

    return '';
  }

  private _navigateToSeed(seed: Seed): Promise<boolean> {
    return this.#router.navigate(['workspace', 'my-referrals', 'referral', seed?.id]);
  }

  private _navigateToBrand(brand: Brand): Promise<boolean> {
    return this.#router.navigate(['workspace', 'rewards', 'brands', brand?.id]);
  }

  private _navigateToProduct(product: Product): Promise<boolean> {
    return this.#router.navigate(['workspace', 'rewards', 'product', product?.id]);
  }

  private async _navigateToGanarbol(ganarbol: Ganarbol): Promise<boolean> {
    const userIdentifier = await this._getSearchResultUserPublicId(ganarbol);
    return this.#router.navigate(['workspace', 'my-forest', 'user', userIdentifier]);
  }

  private async _navigateToGanacuate(ganacuate: Ganacuate): Promise<boolean> {
    const userIdentifier = await this._getSearchResultUserPublicId(ganacuate);
    return this.#router.navigate(['workspace', 'my-forest', 'user', userIdentifier]);
  }

  private _navigateToGanamigo(ganamigo: Ganamigo): Promise<boolean> {
    return this.#router.navigate(['workspace', 'my-referrals', 'referral', ganamigo?.id]);
  }

  private _navigateToFaq(faq: Faq): Promise<boolean> {
    return this.#router.navigate(['workspace', 'faq'], {
      queryParams: { questionId: faq.id, categoryId: faq.category__id }
    });
  }

  private async _getSearchResultUserPublicId(result: Ganarbol | Ganacuate): Promise<string | number> {
    if (result.invited_user__public_id) {
      return result.invited_user__public_id;
    }

    try {
      const user = await firstValueFrom(this.#usersService.getMyForestUserInfoByIdentifier(result.invited_user__id));
      return user.public_id || result.invited_user__id;
    } catch {
      return result.invited_user__id;
    }
  }
}
